import { useDisclosure } from "@chakra-ui/hooks";
import { useCallback, useState, useEffect } from "react";
import { getIntegrations, deleteIntegration } from "./api";
import { Integration } from "./types";
import IntegrationDialog from "./components/IntegrationDialog";
import Search from "./components/Search";
import Loader from "components/Loader";
import { useQueryParams } from "hooks/useQueryParams";
import { TiChevronLeftOutline } from "react-icons/ti";
import { FaChevronCircleRight } from "react-icons/fa";
import IntegrationCard from "./components/IntegrationCard";
import { useConfirmation } from "components/dialog/confirmation";
import toast from "react-hot-toast";
import { handleAxiosAndCaughtError } from "utils";
import { useAuthContext } from "auth/AuthProvider";
import { PERMISSIONS } from "auth/permissions";
import { useTranslation } from "react-i18next";

const IntegrationsView = () => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedIntegration, setSelectedIntegration] =
    useState<Integration | null>(null);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const { params, updateParams } = useQueryParams();
  const itemsPerPage = 8;
  const [totalIntegrations, setTotalIntegrations] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingIntegrationId, setDeletingIntegrationId] = useState<
    string | null
  >(null);
  const { confirm } = useConfirmation();

  const permissions = {
    canDeleteIntegration: true,
  };

  const fetchIntegrations = useCallback(
    async (signal: AbortSignal) => {
      try {
        const skip = (params.page - 1) * itemsPerPage;
        const response = await getIntegrations({
          skip,
          take: itemsPerPage,
        });
        setIntegrations(response.items);
        setTotalIntegrations(response.total);
        setError(null);
      } catch (err) {
        // @ts-expect-error: no prob
        if (err.name === "CanceledError" || signal?.aborted) return;
        setError(t("integrations.failedToFetch"));
      } finally {
        setLoading(false);
      }
    },
    [params.page, params.query, t]
  );

  useEffect(() => {
    const abortController = new AbortController();
    setLoading(true);
    fetchIntegrations(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [fetchIntegrations]);

  const totalPages = Math.ceil(totalIntegrations / itemsPerPage);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      updateParams({ page: newPage });
    }
  };

  const handleUpdate = useCallback(
    (integration: Integration) => {
      setSelectedIntegration(integration);
      onOpen();
    },
    [onOpen]
  );

  const handleDelete = useCallback(
    async (integration: Integration) => {
      const confirmed = await confirm({
        title: t("integrations.deleteIntegration"),
        message: t("integrations.deleteConfirm", { name: integration.name }),
        confirmText: t("common.delete"),
        cancelText: t("common.cancel"),
        variant: "danger",
      });

      if (confirmed) {
        try {
          setDeletingIntegrationId(integration.id);
          await deleteIntegration(integration.id);
          toast.success(t("integrations.deletedSuccessfully"));
          // Refresh the integrations list
          const abortController = new AbortController();
          fetchIntegrations(abortController.signal);
        } catch (err) {
          handleAxiosAndCaughtError(err);
        } finally {
          setDeletingIntegrationId(null);
        }
      }
    },
    [confirm, fetchIntegrations, t]
  );

  const handleClose = useCallback(() => {
    setSelectedIntegration(null);
    onClose();
    const abortController = new AbortController();
    fetchIntegrations(abortController.signal);
  }, [onClose, fetchIntegrations]);

  const handleRetry = () => {
    setLoading(true);
    setError(null);
    const abortController = new AbortController();
    fetchIntegrations(abortController.signal);
  };

  return (
    <div className="relative mt-12 h-[75vh] w-full rounded-[20px]   px-6 sm:mt-0">
      <div className=" mb-5 w-full">
        <Search
          value={params.query}
          onChange={(value) => updateParams({ query: value, page: 1 })}
          onNew={onOpen}
        />
      </div>

      {loading ? (
        <Loader
          txt={t("integrations.loadingIntegrations")}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        />
      ) : error ? (
        <div className="absolute left-1/2 top-1/2 mt-[38px] flex -translate-x-1/2 -translate-y-1/2  flex-col items-center justify-center gap-4">
          <p className="text-lg font-medium text-red-500">{error}</p>
          <button
            onClick={handleRetry}
            className="rounded-lg bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
          >
            {t("common.retry")}
          </button>
        </div>
      ) : integrations.length === 0 ? (
        <div className="absolute left-1/2 top-1/2 mt-[38px] flex -translate-x-1/2 -translate-y-1/2 items-center justify-center">
          <p className="text-lg font-medium text-navy-700 dark:text-white">
            {params.query
              ? t("integrations.noIntegrationsFound", { query: params.query })
              : t("integrations.noIntegrationsAvailable")}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 2xl:grid-cols-3 3xl:grid-cols-4">
          {integrations.map((integration) => (
            <IntegrationCard
              key={integration.id}
              integration={integration}
              onUpdate={handleUpdate}
              onDelete={
                permissions.canDeleteIntegration ? handleDelete : undefined
              }
              isDeleting={deletingIntegrationId === integration.id}
            />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-8 flex items-center justify-center gap-2">
          <button
            onClick={() => handlePageChange(params.page - 1)}
            disabled={params.page === 1}
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <TiChevronLeftOutline />
          </button>

          {[...Array(totalPages)].map((_, index) => (
            <button
              key={index}
              onClick={() => handlePageChange(index + 1)}
              className={`flex h-10 w-10 items-center justify-center rounded-lg border ${
                params.page === index + 1
                  ? "border-blue-500 bg-blue-500 text-white"
                  : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
              }`}
            >
              {index + 1}
            </button>
          ))}

          <button
            onClick={() => handlePageChange(params.page + 1)}
            disabled={params.page === totalPages}
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FaChevronCircleRight />
          </button>
        </div>
      )}
      <IntegrationDialog
        isOpen={isOpen}
        onClose={handleClose}
        integration={selectedIntegration}
      />
    </div>
  );
};

export default IntegrationsView;
